import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { EyeIcon, CopyIcon } from '@/icons';
import { type AdminEvent } from '@/lib/api/cccdAdminClient';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { RowActionsMenu } from '@/components/admin/RowActionsMenu';
import { liveResultsUrl, launchLiveUrl } from '@/components/cccd/ProjectionButtons';

const MonitorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
);
import { StatusPill } from '@/components/cccd/StatusPill';
import Alert from '@/components/ui/alert/Alert';
import DataTable, { type DataTableColumn } from '@/components/ui/table/DataTable';
import { useServerTable } from '@/hooks/useServerTable';
import { TEAM_STATUS_LABELS } from '@/lib/cccdTeamStatus';

function formatDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  const s = new Date(start).toLocaleDateString('es-CO', opts);
  const e = new Date(end).toLocaleDateString('es-CO', opts);
  return `${s} – ${e}`;
}

export default function EventsListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'' | 'active' | 'closed'>('');
  const TOGGLEABLE_COLUMNS = ['eventStartDate', 'registrationStartDate', 'teams', 'isActive'];
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(TOGGLEABLE_COLUMNS);

  const {
    rows: events,
    total: eventsTotal,
    payload,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error,
    reload,
  } = useServerTable<AdminEvent>({
    endpoint: '/cccd/admin/events',
    defaultPageSize: 20,
    defaultSort: { column: 'eventStartDate', dir: 'desc' },
    // El filtro de estado viaja al servidor: el total y la paginación son reales con cualquier cantidad de eventos.
    params: { search: search || undefined, isActive: activeFilter === 'active' ? 'true' : activeFilter === 'closed' ? 'false' : undefined },
    extractRows: (data) => data.events ?? [],
    extractTotal: (data) => data.total ?? 0,
  });
  // Totales globales de todos los eventos (no dependen de la página, la búsqueda ni el filtro).
  const summary = (payload as { summary?: { total: number; active: number; closed: number; teams: number } } | undefined)?.summary;

  const columns: DataTableColumn<AdminEvent>[] = [
    {
      key: 'name',
      label: 'Evento',
      sortable: true,
      render: (ev) => (
        <Link to={`/cccd/events/${ev.id}`} className="block hover:underline">
          <p className="font-medium text-gray-900 dark:text-white">{ev.name}</p>
          <p className="text-xs text-gray-500">{ev.slug}</p>
        </Link>
      ),
    },
    {
      key: 'eventStartDate',
      label: 'Fechas del concurso',
      sortable: true,
      render: (ev) => (
        <span className="text-xs text-gray-500">{formatDateRange(ev.eventStartDate, ev.eventEndDate)}</span>
      ),
    },
    {
      key: 'registrationStartDate',
      label: 'Inscripciones',
      render: (ev) => (
        <span className="text-xs text-gray-500">
          {formatDateRange(ev.registrationStartDate, ev.registrationDeadline)}
        </span>
      ),
    },
    {
      key: 'teams',
      label: 'Equipos',
      align: 'center',
      render: (ev) => (
        <span className="text-xs text-gray-500">
          {ev._count.teams}
          {ev.statusCounts.map((s) => ` · ${s._count} ${TEAM_STATUS_LABELS[s.status as keyof typeof TEAM_STATUS_LABELS] ?? s.status}`).join('')}
        </span>
      ),
    },
    {
      key: 'isActive',
      label: 'Estado',
      align: 'center',
      render: (ev) => {
        // "Activo" es el interruptor de inscripciones; si el cierre ya pasó, el evento sigue marcado abierto pero vencido.
        const overdue = ev.isActive && new Date(ev.registrationDeadline) < new Date();
        return (
          <StatusPill
            tone={overdue ? 'warning' : ev.isActive ? 'success' : 'gray'}
            label={overdue ? 'Vencido' : ev.isActive ? 'Activo' : 'Cerrado'}
          />
        );
      },
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (ev) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              { label: 'Ver evento', icon: <EyeIcon />, to: `/cccd/events/${ev.id}` },
              { label: 'Proyectar Resultados Generales', icon: <MonitorIcon />, onClick: () => window.open(liveResultsUrl(ev.id), '_blank', 'noopener,noreferrer') },
              { label: 'Proyectar Lanzamientos', icon: <MonitorIcon />, onClick: () => window.open(launchLiveUrl(ev.id), '_blank', 'noopener,noreferrer') },
              { label: 'Duplicar', icon: <CopyIcon />, to: `/cccd/events/new?fromId=${ev.id}`, requires: ['cccd:events-national:create', 'cccd:addons:edit'] },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Nacionales']}
      title="Eventos nacionales"
      subtitle="Concursos nacionales CCCD y su calendario de inscripciones"
      tabs={[
        { label: 'Panel', to: '/cccd' },
        { label: 'Nacionales', to: '/cccd/events', active: true },
        { label: 'Regionales', to: '/cccd/regionals' },
      ]}
      metrics={[
        { label: 'Eventos nacionales', value: summary ? String(summary.total) : '—' },
        { label: 'Activos', value: summary ? String(summary.active) : '—', tone: 'good', hint: 'Con la inscripción habilitada (alguno puede haber vencido)' },
        { label: 'Cerrados', value: summary ? String(summary.closed) : '—' },
        { label: 'Equipos inscritos', value: summary ? String(summary.teams) : '—', hint: 'En todos los eventos nacionales' },
      ]}
      toolbar={{
        search: { value: search, onChange: setSearch, placeholder: 'Buscar evento...' },
        filters: [
          {
            type: 'select',
            key: 'active',
            label: 'Estado',
            value: activeFilter,
            onChange: (v) => setActiveFilter(v as '' | 'active' | 'closed'),
            options: [
              { label: 'Todos', value: '' },
              { label: 'Activo', value: 'active' },
              { label: 'Cerrado', value: 'closed' },
            ],
          },
        ],
        columns: [
          { key: 'eventStartDate', label: 'Fechas del concurso' },
          { key: 'registrationStartDate', label: 'Inscripciones' },
          { key: 'teams', label: 'Equipos' },
          { key: 'isActive', label: 'Estado' },
        ],
        visibleColumnKeys,
        onVisibleColumnKeysChange: setVisibleColumnKeys,
        onRefresh: reload,
        refreshing: loading,
        onCreate: () => navigate('/cccd/events/new'),
        createLabel: '+ Nuevo evento',
        requires: 'cccd:events-national:create',
      }}
    >
      {error && <div className="mb-4"><Alert variant="error" title="No se pudieron cargar los eventos" message={error} /></div>}

      <DataTable
        columns={columns.filter((c) => !TOGGLEABLE_COLUMNS.includes(c.key) || visibleColumnKeys.includes(c.key))}
        rows={events}
        rowKey={(ev) => ev.id}
        loading={loading}
        emptyMessage="No se encontraron eventos."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={eventsTotal}
        onPageChange={setPage}
      />
    </ModuleLayout>
  );
}
